import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FiMail, FiPhone, FiHome, FiFileText, FiEdit, FiPlus, FiArrowLeft, FiMoreVertical, FiRefreshCw } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useApiClient } from '../utils/apiClient';
import { useInvoicesInfinite, Invoice } from '../hooks/useInvoicesInfinite';
import { useEstimatesInfinite } from '../hooks/useEstimatesInfinite';
import InvoiceFormWizard from './Invoices/components/InvoiceFormWizard';
import { CustomerFormDrawer } from './Customers/components/CustomerFormDrawer';
import InvoiceTable from './Invoices/components/InvoiceTable';
import EstimateTable from './Estimates/components/EstimateTable';
import PaymentWizard from './Payments/components/PaymentWizard';
import PaymentEditDrawer from './Payments/components/PaymentEditDrawer';
import CustomerPricingModal from './Customers/components/CustomerPricingModal';
import { ConfirmationDialog } from '../components/ui/ConfirmationDialog';

import { Customer } from '../hooks/useCustomers';

// Customer fields are returned directly by backend on invoice objects

interface Payment {
  id: string;
  amount: number;
  date: string;
  method: 'cash' | 'check' | 'card' | 'credit_card' | 'bank_transfer' | 'zelle';
  reference?: string;
  memo?: string;
  status: 'active' | 'voided';
  invoice_id?: string;
  invoice_numbers?: string;
  customer_id: string;
  applications?: Array<{
    invoice_id: string;
    invoice_number: string;
    amount: number;
    origin: string;
  }>;
  applied_amount?: number;
  unapplied_credit?: number;
  has_credit?: boolean;
}

interface PaymentForm {
  amount: number;
  date: string;
  method: 'cash' | 'check' | 'card' | 'bank_transfer' | 'credit' | 'zelle';
  reference: string;
  memo: string;
  selectedInvoices: string[];
}

export default function CustomerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token, currentBusiness, isCookieMode, API_BASE_URL, csrfToken } = useAuth();
  const api = useApiClient();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState<Customer | null>(null);
  const [saving, setSaving] = useState(false);
  const [isPricingModalOpen, setIsPricingModalOpen] = useState(false);
  const [isWizardModalOpen, setIsWizardModalOpen] = useState(false);
  const [isEstimateWizardOpen, setIsEstimateWizardOpen] = useState(false);
  const [isPaymentWizardOpen, setIsPaymentWizardOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null);
  const [isPaymentEditOpen, setIsPaymentEditOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedItem, setSelectedItem] = useState<{ type: 'invoice' | 'payment'; item: any } | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeError, setFinanceError] = useState('');
  const [customerCredit, setCustomerCredit] = useState(0);
  const [isBusinessContextReady, setIsBusinessContextReady] = useState(false);
  
  // Confirmation dialog states
  const [showPaymentDeleteConfirm, setShowPaymentDeleteConfirm] = useState(false);
  const [showInvoiceDeleteConfirm, setShowInvoiceDeleteConfirm] = useState(false);
  const [showEstimateStatusWarning, setShowEstimateStatusWarning] = useState(false);
  const [pendingEstimateStatusChange, setPendingEstimateStatusChange] = useState<{ estimateId: string; status: 'pending' | 'approved' | 'rejected' } | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] = useState<{ type: 'invoice' | 'payment'; item: any } | null>(null);

  // Use the infinite hook for invoices with customer filtering
  const {
    invoices: fetchedInvoices,
    error: fetchError,
    isLoading: invoicesLoading,
    isLoadingMore,
    isReachingEnd,
    loadMore,
    mutate: mutateInvoices,
    isEmpty: invoicesEmpty,
  } = useInvoicesInfinite('', 'all', id); // Filter by customer ID

  const {
    estimates: fetchedEstimates,
    isLoading: estimatesLoading,
    mutate: mutateEstimates,
    isEmpty: estimatesEmpty,
  } = useEstimatesInfinite('', 'all', id);

  // Fetch SBAL invoice by number and hold it locally for this view
  const [sbalInvoice, setSbalInvoice] = useState<any | null>(null);
  // Reusable fetch for SBAL invoice by number
  const fetchSbal = async () => {
    if (!currentBusiness || !id) return;
    try {
      const headers: any = { };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const number = `SBAL-${id}`;
      const res = await axios.get(`${API_BASE_URL}/invoices/by-number/${encodeURIComponent(number)}`, {
        headers,
        withCredentials: isCookieMode,
      });
      setSbalInvoice(res.data || null);
    } catch (_err: any) {
      // 404 or other: SBAL may not exist — clear any stale value
      setSbalInvoice(null);
    }
  };
  useEffect(() => {
    fetchSbal();
  }, [currentBusiness, id, API_BASE_URL, isCookieMode, token, csrfToken]);

  // Wait for business context to be established before fetching data
  useEffect(() => {
    const fetchCustomer = async () => {
      setLoading(true);
      setError('');
      try {
        const headers: any = {
        };
        
        // Only add Authorization header if not in cookie mode
        if (!isCookieMode && token) {
          headers.Authorization = `Bearer ${token}`;
        }
        
        const res = await axios.get(`${API_BASE_URL}/customers/${id}`, {
          headers,
          withCredentials: isCookieMode, // This sends cookies with the request
        });
        setCustomer(res.data);
        setIsBusinessContextReady(true);
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to fetch customer');
      } finally {
        setLoading(false);
      }
    };

    if (currentBusiness && id) {
      fetchCustomer();
    }
  }, [currentBusiness, id, token, isCookieMode, API_BASE_URL]);

  // Explicit refetch helper used after payments are applied so balances and SBAL conversion reflect immediately
  const refreshCustomer = async () => {
    if (!id) return;
    try {
      const headers: any = { };
      if (!isCookieMode && token) headers.Authorization = `Bearer ${token}`;
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
      const res = await axios.get(`${API_BASE_URL}/customers/${id}`, {
        headers,
        withCredentials: isCookieMode,
      });
      setCustomer(res.data);
    } catch (_e) {
      // Non-fatal; UI will still update via other revalidations
    }
  };

  // Fetch customer credit balance when business context is ready
  useEffect(() => {
    if (isBusinessContextReady && id && currentBusiness) {
      const fetchCredit = async () => {
        try {
          const headers: any = {
          };
          
          if (!isCookieMode && token) {
            headers.Authorization = `Bearer ${token}`;
          }
          
          const creditRes = await axios.get(`${API_BASE_URL}/payments/customer-credits/${id}`, {
            headers,
            withCredentials: isCookieMode,
          });
          
          setCustomerCredit(creditRes.data.balance || 0);
        } catch (err) {
          console.error('Failed to fetch customer credit:', err);
        }
      };
      
      fetchCredit();
    }
  }, [isBusinessContextReady, id, currentBusiness, token, isCookieMode, API_BASE_URL]);

  // Function to refresh customer credit balance
  const fetchCustomerCredit = async () => {
    if (!isBusinessContextReady || !id || !currentBusiness) return;
    
    try {
      const headers: any = {
      };
      
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const creditRes = await axios.get(`${API_BASE_URL}/payments/customer-credits/${id}`, {
        headers,
        withCredentials: isCookieMode,
      });
      
      setCustomerCredit(creditRes.data.balance || 0);
    } catch (err) {
      console.error('Failed to fetch customer credit:', err);
    }
  };

  // Fetch payments for this customer (keeping this separate for now)
  const fetchPayments = async () => {
    if (!isBusinessContextReady || !id || !currentBusiness) return;
    setFinanceLoading(true);
    setFinanceError('');
    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      
      const payRes = await axios.get(`${API_BASE_URL}/payments?customer_id=${id}`, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });
      const normalizedPayments = (payRes.data || []).map((payment: any) => ({
        ...payment,
        amount: Number(payment.amount || 0),
        applied_amount: Number(payment.applied_amount || 0),
        unapplied_credit: Number(payment.unapplied_credit || 0),
        has_credit: Boolean(payment.has_credit),
        applications: Array.isArray(payment.applications)
          ? payment.applications.map((app: any) => ({
              ...app,
              amount: Number(app.amount || 0)
            }))
          : []
      }));
      setPayments(normalizedPayments);
    } catch (err: any) {
      setFinanceError(err.response?.data?.error || 'Failed to fetch payments');
    } finally {
      setFinanceLoading(false);
    }
  };

  // Fetch payments when business context is ready
  useEffect(() => {
    if (isBusinessContextReady && id && currentBusiness) {
      fetchPayments();
    }
  }, [isBusinessContextReady, id, currentBusiness, token, isCookieMode, API_BASE_URL]);

  // Handle escape key to close menu
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        handleMenuClose();
      }
    };

    if (menuPosition) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [menuPosition]);

  // Debug logging for menu state
  useEffect(() => {
    if (menuPosition && selectedItem) {
  
    }
  }, [menuPosition, selectedItem]);

  // Debug logging for payment edit state
  useEffect(() => {
    if (isPaymentEditOpen) {
  
    }
  }, [isPaymentEditOpen, selectedPayment]);

  const openEditModal = () => {
    if (customer) {
      setEditForm({ ...customer });
      setEditModalOpen(true);
    }
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditForm(null);
  };

  const handleEditSave = async (updatedCustomer: Partial<Customer>) => {
    if (!currentBusiness) return;
    if (!isCookieMode && !token) return;
    setSaving(true);
    setError('');

    try {
      const updated = await api.put<Customer>(`/customers/${id}`, updatedCustomer);
      setCustomer(updated as Customer);
      setEditModalOpen(false);
      setEditForm(null);
    } catch (err: any) {
      setError(err.message || 'Failed to update customer');
    } finally {
      setSaving(false);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent, type: 'invoice' | 'payment', item: any) => {
    // Don't allow menu for synthetic payments
    if (type === 'payment' && item.id === 'starting-balance-credit') {
      return;
    }
    
    event.stopPropagation();
    setMenuPosition({ x: event.clientX, y: event.clientY });
    setSelectedItem({ type, item });
  };

  const handleInvoiceDelete = async (id: string) => {
    if (id === 'starting-balance') return; // non-clickable/non-deletable
    
    // Set the pending delete item and show confirmation dialog
    setPendingDeleteItem({ type: 'invoice', item: { id } });
    setShowInvoiceDeleteConfirm(true);
  };

  const handleInvoiceDeleteConfirm = async () => {
    if (!pendingDeleteItem || pendingDeleteItem.type !== 'invoice') return;
    
    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }

      // Add CSRF token for DELETE (required in cookie mode)
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      await axios.delete(`${API_BASE_URL}/invoices/${pendingDeleteItem.item.id}`, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });
      // Refresh the invoices list
      mutateInvoices();
    } catch (err: any) {
      console.error('Failed to delete invoice:', err);
    } finally {
      setShowInvoiceDeleteConfirm(false);
      setPendingDeleteItem(null);
    }
  };

  const handleInvoiceEdit = (id: string) => {
    if (id === 'starting-balance') return; // non-clickable
    navigate(`/invoices/${id}/edit`);
  };

  const handleInvoiceView = (id: string) => {
    if (id === 'starting-balance') return; // non-clickable
    navigate(`/invoices/${id}`);
  };

  const handleInvoiceDownload = (id: string) => {
    if (id === 'starting-balance') return; // non-clickable
    // Download functionality will be handled by InvoiceActions component
            // Download invoice functionality not implemented yet
  };

  const handleInvoiceSend = (id: string) => {
    if (id === 'starting-balance') return; // non-clickable
    // Send functionality will be handled by InvoiceActions component
            // Send invoice functionality not implemented yet
  };

  const handleEstimateDelete = async (estimateId: string) => {
    if (!window.confirm('Are you sure you want to delete this estimate?')) return;
    try {
      const headers: any = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      await axios.delete(`${API_BASE_URL}/estimates/${estimateId}`, {
        headers,
        withCredentials: isCookieMode,
      });
      mutateEstimates();
    } catch (err) {
      console.error('Failed to delete estimate:', err);
    }
  };

  const handleEstimateEdit = (estimateId: string) => {
    navigate(`/estimates/${estimateId}/edit`);
  };

  const handleEstimateView = (estimateId: string) => {
    navigate(`/estimates/${estimateId}`);
  };

  const handleEstimateStatusChange = async (estimateId: string, status: 'pending' | 'approved' | 'rejected') => {
    const estimate = fetchedEstimates.find((entry) => String(entry.id) === String(estimateId));
    if (status === 'approved' && !estimate?.converted_invoice_id) {
      setPendingEstimateStatusChange({ estimateId, status });
      setShowEstimateStatusWarning(true);
      return;
    }
    try {
      const headers: any = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      await axios.put(`${API_BASE_URL}/estimates/${estimateId}`, {
        status,
      }, {
        headers,
        withCredentials: isCookieMode,
      });
      mutateEstimates();
    } catch (err) {
      console.error('Failed to update estimate status:', err);
    }
  };

  const handleEstimateStatusWarningConfirm = async () => {
    if (!pendingEstimateStatusChange) return;
    try {
      const headers: any = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      if (pendingEstimateStatusChange.status === 'approved') {
        const response = await axios.post(`${API_BASE_URL}/estimates/${pendingEstimateStatusChange.estimateId}/convert-to-invoice`, {}, {
          headers,
          withCredentials: isCookieMode,
        });
        mutateInvoices();
        const invoiceId = response.data?.invoiceId;
        if (invoiceId) {
          navigate(`/invoices/${invoiceId}`);
          return;
        }
      } else {
        await axios.put(`${API_BASE_URL}/estimates/${pendingEstimateStatusChange.estimateId}`, {
          status: pendingEstimateStatusChange.status,
        }, {
          headers,
          withCredentials: isCookieMode,
        });
      }
      mutateEstimates();
    } catch (err) {
      console.error('Failed to update estimate status:', err);
    } finally {
      setShowEstimateStatusWarning(false);
      setPendingEstimateStatusChange(null);
    }
  };

  const handleEstimateConvert = async (estimateId: string) => {
    if (!window.confirm('This will convert this estimate into a new invoice, are you sure?')) return;
    try {
      const headers: any = {};
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
      const response = await axios.post(`${API_BASE_URL}/estimates/${estimateId}/convert-to-invoice`, {}, {
        headers,
        withCredentials: isCookieMode,
      });
      mutateEstimates();
      mutateInvoices();
      const invoiceId = response.data?.invoiceId;
      if (invoiceId) {
        navigate(`/invoices/${invoiceId}`);
      }
    } catch (err) {
      console.error('Failed to convert estimate:', err);
    }
  };

  const handlePaymentEdit = (payment: any) => {
    setSelectedPayment(payment);
    setIsPaymentEditOpen(true);
  };

  const handlePaymentSave = async (payment: PaymentForm) => {
    if (!currentBusiness) return;
    if (!isCookieMode && !token) return;

    try {
      const headers: any = {
      };
      
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      const paymentData = {
        customer_id: id,
        amount: payment.amount,
        date: payment.date,
        method: payment.method,
        reference: payment.reference,
        memo: payment.memo,
        invoice_ids: payment.selectedInvoices,
      };

      await axios.post(`${API_BASE_URL}/payments`, paymentData, {
        headers,
        withCredentials: isCookieMode,
      });

      // Refresh data
      await fetchPayments();
      mutateInvoices();
      setIsPaymentWizardOpen(false);
    } catch (err: any) {
      console.error('Failed to create payment:', err);
      alert(`Failed to create payment: ${err.response?.data?.error || err.message}`);
    }
  };

  const handlePaymentDelete = async (paymentId: string) => {
    if (!currentBusiness) return;
    if (!isCookieMode && !token) return;

    try {
      const headers: any = {
      };
      
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      await axios.delete(`${API_BASE_URL}/payments/${paymentId}`, {
        headers,
        withCredentials: isCookieMode,
      });

      // Refresh data
      await fetchPayments();
      mutateInvoices();
      fetchCustomerCredit(); // Refresh customer credit balance
      setIsPaymentEditOpen(false);
    } catch (err: any) {
      console.error('Failed to delete payment:', err);
      alert(`Failed to delete payment: ${err.response?.data?.error || err.message}`);
    }
  };

  const handleRecalculateBalances = async () => {
    if (!currentBusiness) return;
    if (!isCookieMode && !token) return;

    try {
      const headers: any = {
      };
      
      // Only add Authorization header if not in cookie mode
      if (!isCookieMode && token) {
        headers.Authorization = `Bearer ${token}`;
      }
      // Add CSRF token for unsafe method
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }

      await axios.post(`${API_BASE_URL}/payments/recalculate-balances/${id}`, {}, {
        headers,
        withCredentials: isCookieMode, // This sends cookies with the request
      });
      alert('Invoice balances recalculated successfully!');
      mutateInvoices(); // Refresh invoices to reflect new balances
      fetchCustomerCredit(); // Refresh customer credit balance
    } catch (err: any) {
      alert(`Failed to recalculate invoice balances: ${err.response?.data?.error || err.message}`);
      console.error('Failed to recalculate invoice balances:', err);
    }
  };

  const handleMenuClose = () => {
    setMenuPosition(null);
    setSelectedItem(null);
  };

  const handleEdit = () => {
    if (!selectedItem) return;
    
    if (selectedItem.type === 'payment') {
      // Don't allow editing synthetic payments
      if (selectedItem.item.id === 'starting-balance-credit') {
        alert('Starting balance credits cannot be edited.');
        handleMenuClose();
        return;
      }
      setSelectedPayment(selectedItem.item);
      setIsPaymentEditOpen(true);
    } else if (selectedItem.type === 'invoice') {
      // Handle invoice editing - navigate to invoice edit page
      navigate(`/invoices/${selectedItem.item.id}/edit`);
    }
    
    handleMenuClose();
  };

  const handleDelete = async () => {
    if (!selectedItem) return;
    
    setPendingDeleteItem(selectedItem);
    
    if (selectedItem.type === 'payment') {
      setShowPaymentDeleteConfirm(true);
    } else if (selectedItem.type === 'invoice') {
      setShowInvoiceDeleteConfirm(true);
    }
    
    handleMenuClose();
  };

  const handlePaymentEditSave = (updatedPayment: Payment) => {
    // This handler is for the PaymentEditDrawer which expects a Payment object
    // The actual save logic is handled by the drawer itself, we just need to refresh data
    fetchPayments();
    mutateInvoices();
    fetchCustomerCredit(); // Refresh customer credit balance
  };

  const handlePaymentDeleteConfirm = async () => {
    if (pendingDeleteItem?.type === 'payment') {
      await handlePaymentDelete(pendingDeleteItem.item.id);
    }
    setShowPaymentDeleteConfirm(false);
    setPendingDeleteItem(null);
  };

  const handleCancelDelete = () => {
    setShowPaymentDeleteConfirm(false);
    setShowInvoiceDeleteConfirm(false);
    setPendingDeleteItem(null);
  };

  if (loading) return <div className="p-8 text-center text-neutral-600">Loading...</div>;
  if (error) return <div className="p-8 text-center text-red-600">{error}</div>;
  if (!customer) return <div className="p-8 text-center text-neutral-600">No customer found.</div>;
  if (!isBusinessContextReady) return <div className="p-8 text-center text-neutral-600">Loading business context...</div>;

  // Compute financial overview with starting balance and credits
  const sb = Number(customer.starting_balance || 0);
  
  // Include SBAL invoice for balance math, or fallback to synthetic if starting_balance > 0 and SBAL not created yet
  const invoicesForBalanceBase = [...fetchedInvoices];
  const invoicesForBalance = sbalInvoice
    ? [...invoicesForBalanceBase, sbalInvoice]
    : (sb > 0 ? [...invoicesForBalanceBase, { status: 'overdue', balance_due: sb } as any] : invoicesForBalanceBase);
  
  const outstandingBalance = invoicesForBalance
    .filter(inv => inv.status === 'overdue' || inv.status === 'partial_over')
    .reduce((sum, inv) => sum + (Number(inv.balance_due) || 0), 0);
  
  const currentBalance = invoicesForBalance
    .filter(inv => inv.status === 'overdue' || inv.status === 'open' || inv.status === 'partial_open' || inv.status === 'partial_over')
    .reduce((sum, inv) => sum + (Number(inv.balance_due) || 0), 0) - customerCredit; // Subtract credits from balance

  const lastInvoice = fetchedInvoices.length > 0 ? [...fetchedInvoices].sort((a, b) => new Date(b.issue_date).getTime() - new Date(a.issue_date).getTime())[0] : null;
  const lastPayment = payments.length > 0 ? [...payments].sort((a, b) => new Date(b.date).getTime() - new Date(b.date).getTime())[0] : null;

  // Build synthetic entries for recent activity
  const syntheticInvoices: any[] = [];
  if (sb > 0) {
    syntheticInvoices.push({
      id: 'starting-balance',
      number: 'STARTING ACCOUNT BALANCE',
      display_name: customer.display_name,
      first_name: customer.first_name,
      last_name: customer.last_name,
      company_name: customer.company_name,
      email: customer.email,
      issue_date: '1970-01-01',
      due_date: '1970-01-01',
      status: 'overdue',
      items: [],
      total_amount: sb,
      total: sb,
    });
  }

  // Define a proper type for synthetic payments
  interface SyntheticPayment {
    id: string;
    customer_id: string;
    amount: number;
    date: string;
    method: 'credit';
    memo: string;
    status: 'active';
  }

  const syntheticPayments: SyntheticPayment[] = [];
  if (sb < 0) {
    syntheticPayments.push({
      id: 'starting-balance-credit',
      customer_id: customer.id || '',
      amount: Math.abs(sb),
      date: customer.created_at ? new Date(customer.created_at).toISOString().slice(0, 10) : new Date().toISOString().slice(0, 10),
      method: 'credit',
      memo: 'STARTING ACCOUNT BALANCE',
      status: 'active',
    });
  }

  // Merge synthetic invoices at the bottom (older date ensures bottom)
  const invoicesForTable = [...fetchedInvoices, ...syntheticInvoices, ...(sbalInvoice ? [sbalInvoice] : [])];

  // Payments: show the full customer payment history in newest-first order.
  const paymentsForList = [...syntheticPayments, ...payments]
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  // Helper function to safely get invoice numbers
  const getInvoiceNumbers = (payment: Payment | SyntheticPayment): string => {
    if ('invoice_numbers' in payment && payment.invoice_numbers) {
      return payment.invoice_numbers;
    }
    return '';
  };

  const getPaymentApplications = (payment: Payment | SyntheticPayment) => {
    if ('applications' in payment && Array.isArray(payment.applications)) {
      return payment.applications;
    }
    return [];
  };

  const getUnappliedCredit = (payment: Payment | SyntheticPayment): number => {
    if ('unapplied_credit' in payment) {
      return Number(payment.unapplied_credit || 0);
    }
    return payment.memo === 'STARTING ACCOUNT BALANCE' ? Number(payment.amount || 0) : 0;
  };

  const getPaymentMethodLabel = (payment: Payment | SyntheticPayment): string => {
    if (payment.method === 'credit') return 'Credit';
    if (payment.method === 'bank_transfer') return 'Bank transfer';
    if (payment.method === 'zelle') return 'Zelle';
    if (payment.method === 'card' || payment.method === 'credit_card') return 'Card';
    if (payment.method === 'check') return 'Check';
    if (payment.method === 'cash') return 'Cash';
    return payment.method;
  };

  const getPaymentReferenceLabel = (payment: Payment | SyntheticPayment): string | null => {
    if (!('reference' in payment) || !payment.reference) return null;
    if (payment.method === 'check') return `Check #${payment.reference}`;
    if (payment.method === 'card' || payment.method === 'credit_card') return `Card ref ${payment.reference}`;
    if (payment.method === 'bank_transfer') return `Transfer ref ${payment.reference}`;
    if (payment.method === 'zelle') return `Zelle ref ${payment.reference}`;
    return `Reference ${payment.reference}`;
  };

  const formatPaymentDate = (date: string): string =>
    new Date(date).toLocaleDateString(undefined, { timeZone: 'UTC' });

  // Ensure payments have customer_id for PaymentEditDrawer compatibility
  const paymentsWithCustomerId = payments.map(payment => ({
    ...payment,
    customer_id: payment.customer_id || customer.id || ''
  }));

  return (
    <div className="mx-auto max-w-7xl px-6 lg:px-8 py-8">
      <header className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-gray-900 dark:text-white">Customer Details</h1>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={openEditModal}
            className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          >
            <FiEdit className="inline mr-2" />
            Edit
          </button>
          <button
            onClick={() => setIsPricingModalOpen(true)}
            className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          >
            <FiEdit className="inline mr-2" />
            Edit Customer Prices
          </button>
          <button
            onClick={() => setIsWizardModalOpen(true)}
            className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          >
            <FiPlus className="inline mr-2" />
            Create Invoice
          </button>
          <button
            onClick={() => setIsEstimateWizardOpen(true)}
            className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          >
            <FiPlus className="inline mr-2" />
            Create Estimate
          </button>
          <button
            onClick={() => setIsPaymentWizardOpen(true)}
            className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          >
            <FiPlus className="inline mr-2" />
            Create Payment
          </button>
          <button
            onClick={handleRecalculateBalances}
            className="rounded-xl bg-sedra px-4 py-2 text-sm font-semibold text-white hover:bg-sedra-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
          >
            <FiRefreshCw className="inline mr-2" />
            Recalculate Balances
          </button>
        </div>
      </header>

      {/* Customer Info Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:bg-neutral-900 dark:border-neutral-800 mb-6">
        <h2 className="text-xl font-semibold mb-4 text-neutral-900 dark:text-neutral-50">Customer Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-3">
              {customer.first_name || customer.last_name ? `${customer.first_name || ''} ${customer.last_name || ''}`.trim() : 'No Name'}
            </h3>
            <div className="flex items-center mb-2">
              <span className="font-medium text-neutral-900 dark:text-neutral-50 mr-2">Customer ID:</span>
              <span className="text-neutral-700 dark:text-neutral-300">{customer.id}</span>
            </div>
            {customer.email && (
              <div className="flex items-center mb-2">
                <FiMail className="text-neutral-500 mr-2" />
                <span className="text-neutral-700 dark:text-neutral-300">{customer.email}</span>
              </div>
            )}
            {customer.phone && (
              <div className="flex items-center mb-2">
                <FiPhone className="text-neutral-500 mr-2" />
                <span className="text-neutral-700 dark:text-neutral-300">{customer.phone}</span>
              </div>
            )}
            <div className="flex items-center mb-2">
              <span className="font-medium text-neutral-900 dark:text-neutral-50 mr-2">Tax Exempt:</span>
              <span className="text-neutral-700 dark:text-neutral-300">{customer.tax_exempt ? 'Yes' : 'No'}</span>
            </div>
          </div>
          
          <div>
            <div className="flex items-center mb-3">
              <FiHome className="text-neutral-500 mr-2" />
              <span className="font-medium text-neutral-900 dark:text-neutral-50">Billing Address:</span>
            </div>
            <div className="ml-6 text-neutral-700 dark:text-neutral-300">
              {customer.billing_addr_line1 && <div>{customer.billing_addr_line1}</div>}
              {customer.billing_addr_city && customer.billing_addr_state && customer.billing_addr_zip && (
                <div>{customer.billing_addr_city}, {customer.billing_addr_state} {customer.billing_addr_zip}</div>
              )}
            </div>
            {customer.notes && (
              <div className="mt-4">
                <div className="flex items-center mb-2">
                  <FiFileText className="text-neutral-500 mr-2" />
                  <span className="font-medium text-neutral-900 dark:text-neutral-50">Notes:</span>
                </div>
                <div className="ml-6 text-neutral-700 dark:text-neutral-300">{customer.notes}</div>
              </div>
            )}
          </div>
          {/* Shipping Address Display */}
          <div>
            <h3 className="text-lg font-medium text-neutral-900 dark:text-neutral-50 mb-3">Shipping Address</h3>
            {customer && (
              <div className="text-sm text-neutral-700 dark:text-neutral-300 space-y-1">
                {customer.shipping_same_as_billing ? (
                  <div>
                    <div className="italic text-neutral-500">Same as billing</div>
                    <div>{customer.billing_addr_line1}</div>
                    <div>{[customer.billing_addr_city, customer.billing_addr_state, customer.billing_addr_zip].filter(Boolean).join(', ')}</div>
                  </div>
                ) : (
                  <div>
                    {(customer as any).shipping_addr_line1 ? <div>{(customer as any).shipping_addr_line1}</div> : <div className="text-neutral-500">No shipping address</div>}
                    <div>{[(customer as any).shipping_addr_city, (customer as any).shipping_addr_state, (customer as any).shipping_addr_zip].filter(Boolean).join(', ')}</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Financial Overview */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-neutral-900 dark:text-neutral-50">Financial Overview</h2>
        {financeLoading ? (
          <div className="text-center text-neutral-600">Loading financial data...</div>
        ) : financeError ? (
          <div className="text-center text-red-600">{financeError}</div>
        ) : (
          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-50 mb-4">Financial Overview</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">Outstanding Balance</p>
                <p className={`text-2xl font-bold ${outstandingBalance > 0 ? 'text-red-600' : 'text-neutral-900 dark:text-neutral-50'}`}>
                  ${outstandingBalance.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-1">
                  {currentBalance < 0 ? 'Available Credit' : 'Current Balance'}
                </p>
                <p className={`text-2xl font-bold ${
                  currentBalance < 0 
                    ? 'text-green-600' 
                    : currentBalance > 0 
                      ? 'text-red-600' 
                      : 'text-neutral-900 dark:text-neutral-50'
                }`}>
                  {currentBalance < 0 
                    ? `(${Math.abs(currentBalance).toFixed(2)})` 
                    : `$${currentBalance.toFixed(2)}`
                  }
                </p>
              </div>
            </div>
            {customerCredit > 0 && (
              <div className="mt-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-md">
                <p className="text-sm text-green-800 dark:text-green-200">
                  💳 Available Credit: ${customerCredit.toFixed(2)}
                </p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="mb-6">
        <h2 className="text-xl font-semibold mb-4 text-neutral-900 dark:text-neutral-50">Recent Activity</h2>
        {financeLoading ? (
          <div className="text-center text-neutral-600">Loading activity...</div>
        ) : financeError ? (
          <div className="text-center text-red-600">{financeError}</div>
        ) : (
          <div className="space-y-4">
            <div>
              <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-neutral-800">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Invoices</h3>
                </div>
                {invoicesLoading ? (
                  <div className="p-4 text-center text-neutral-600">Loading invoices...</div>
                ) : fetchError ? (
                  <div className="p-4 text-center text-red-600">Failed to load invoices</div>
                ) : invoicesEmpty && syntheticInvoices.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-400">No invoices found for this customer.</div>
                ) : (
                  <div className="p-4">
                    <InvoiceTable
                      invoices={invoicesForTable.map((invoice: any) => ({
                        id: String(invoice.id),
                        customer_id: invoice.customer_id || '',
                        number: invoice.number,
                        display_name: invoice.display_name,
                        first_name: invoice.first_name,
                        last_name: invoice.last_name,
                        company_name: invoice.company_name,
                        email: invoice.email,
                        issue_date: invoice.issue_date,
                        due_date: invoice.due_date,
                        status: invoice.status,
                        sub_total: invoice.sub_total || 0,
                        tax_total: invoice.tax_total || 0,
                        balance_due: (invoice.balance_due != null ? invoice.balance_due : (invoice.total_amount ?? invoice.total ?? 0)) || 0,
                        items: [], // Backend doesn't return items in list view
                        // total_amount should represent the original invoice amount (sub_total + tax_total)
                        total_amount: (invoice.sub_total != null || invoice.tax_total != null)
                          ? (invoice.sub_total || 0) + (invoice.tax_total || 0)
                          : (invoice.total_amount ?? invoice.total ?? 0),
                        total: (invoice.sub_total != null || invoice.tax_total != null)
                          ? (invoice.sub_total || 0) + (invoice.tax_total || 0)
                          : (invoice.total_amount ?? invoice.total ?? 0), // Preserve synthetic total
                      }))}
                      onDelete={handleInvoiceDelete}
                      onEdit={handleInvoiceEdit}
                      onView={handleInvoiceView}
                      onDownload={handleInvoiceDownload}
                      onSend={handleInvoiceSend}
                      loading={invoicesLoading}
                    />

                    {/* Load More Button */}
                    {!isReachingEnd && !invoicesEmpty && (
                      <div className="mt-6 text-center">
                        <button
                          onClick={loadMore}
                          disabled={isLoadingMore}
                          className="inline-flex items-center px-4 py-2 border border-neutral-300 dark:border-neutral-600 rounded-md shadow-sm text-sm font-medium text-neutral-700 dark:text-neutral-300 bg-white dark:bg-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-600 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {isLoadingMore ? 'Loading...' : 'Load More'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </section>
            </div>

            <div>
              <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-neutral-800">
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Payments</h3>
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                      Payment activity with method and reference details.
                    </p>
                  </div>
                  <div className="rounded-full bg-sedra/10 px-3 py-1 text-xs font-semibold text-sedra">
                    {paymentsForList.length} shown
                  </div>
                </div>
                {paymentsForList.length === 0 ? (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-400">No payments.</div>
                ) : (
                  <div className="max-h-[70vh] overflow-y-auto p-4 space-y-2">
                    {paymentsForList.map(pay => {
                      const paymentApplications = getPaymentApplications(pay);
                      const unappliedCredit = getUnappliedCredit(pay);
                      const hasCredit = unappliedCredit > 0.009;
                      return (
                      <div
                        key={pay.id}
                        className="rounded-xl border border-gray-200 bg-white px-4 py-3 shadow-sm transition-colors hover:border-sedra/20 hover:bg-sedra/5 dark:bg-neutral-900 dark:border-neutral-800 dark:hover:border-sedra/30 dark:hover:bg-sedra/10"
                      >
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-[160px_minmax(0,1fr)_220px_auto] md:items-center">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2 md:flex-col md:items-start md:gap-1">
                              <div className={`text-xl font-extrabold tabular-nums ${hasCredit ? 'text-emerald-700 dark:text-emerald-300' : 'text-neutral-900 dark:text-neutral-50'}`}>
                                ${pay.amount.toFixed(2)}
                              </div>
                              <span className="rounded-full border border-gray-200 bg-gray-50 px-2.5 py-1 text-xs font-semibold text-gray-700 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-200">
                                {getPaymentMethodLabel(pay)}
                              </span>
                              {pay.status === 'voided' && (
                                <span className="rounded-full border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-semibold text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                                  Voided
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-50">
                              {pay.memo === 'STARTING ACCOUNT BALANCE'
                                ? 'Starting account balance credit'
                                : paymentApplications.length > 0
                                  ? `Applied to ${getInvoiceNumbers(pay)}`
                                  : 'Unapplied payment'}
                            </div>
                            {paymentApplications.length > 0 && (
                              <div className="mt-2 space-y-1">
                                {paymentApplications.map(app => (
                                  <div key={`${pay.id}-${app.invoice_id}`} className="flex max-w-md items-center justify-between gap-3 text-xs text-neutral-600 dark:text-neutral-400">
                                    <span className="truncate">#{app.invoice_number}</span>
                                    <span className="shrink-0 font-medium text-neutral-800 dark:text-neutral-200">${Number(app.amount || 0).toFixed(2)}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                            {hasCredit && (
                              <div className="mt-2 text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                                Credit remaining: ${unappliedCredit.toFixed(2)}
                              </div>
                            )}
                            {'memo' in pay && pay.memo && pay.memo !== 'STARTING ACCOUNT BALANCE' && (
                              <div className="mt-1 truncate text-xs text-neutral-500 dark:text-neutral-400">
                                Memo: {pay.memo}
                              </div>
                            )}
                            {'invoice_numbers' in pay && pay.invoice_numbers && (
                              <div className="mt-1 truncate text-xs text-neutral-500 dark:text-neutral-400">
                                Invoice reference: <span className="font-medium text-neutral-700 dark:text-neutral-200">{pay.invoice_numbers}</span>
                              </div>
                            )}
                          </div>

                          <div className="min-w-0 text-xs text-neutral-600 dark:text-neutral-400 md:text-right">
                            <div>Date: {formatPaymentDate(pay.date)}</div>
                            {getPaymentReferenceLabel(pay) && (
                              <div className="mt-1 font-medium text-neutral-700 dark:text-neutral-300">
                                {getPaymentReferenceLabel(pay)}
                              </div>
                            )}
                          </div>

                          <div className="flex items-start justify-end">
                            {pay.id !== 'starting-balance-credit' && (
                              <button
                                onClick={(e) => handleMenuOpen(e, 'payment', pay)}
                                className="rounded-full p-2 transition-colors hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:hover:bg-blue-900/20"
                                title="Payment Options"
                                aria-label="Payment options"
                              >
                                <FiMoreVertical className="h-4 w-4 text-neutral-500 hover:text-blue-600 dark:hover:text-blue-400" />
                              </button>
                            )}
                          </div>
                        </div>

                        {pay.memo === 'STARTING ACCOUNT BALANCE' && (
                          <div className="mt-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
                            This credit came from the customer&apos;s starting account balance.
                          </div>
                        )}
                      </div>
                    )})}
                  </div>
                )}
              </section>
            </div>
            
            <div>
              <section className="rounded-2xl border border-gray-200 bg-white shadow-sm dark:bg-neutral-900 dark:border-neutral-800">
                <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-neutral-800">
                  <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-600 dark:text-gray-400">Estimates</h3>
                </div>
                {estimatesLoading ? (
                  <div className="p-4 text-center text-neutral-600">Loading estimates...</div>
                ) : estimatesEmpty ? (
                  <div className="py-12 text-center text-gray-500 dark:text-gray-400">No estimates found for this customer.</div>
                ) : (
                  <div className="p-4">
                    <EstimateTable
                      estimates={fetchedEstimates.map((estimate: any) => ({
                        ...estimate,
                        id: String(estimate.id),
                        customer_id: estimate.customer_id || '',
                        sub_total: Number(estimate.sub_total || 0),
                        tax_total: Number(estimate.tax_total || 0),
                        total_amount: Number(estimate.total_amount || 0),
                      }))}
                      loading={estimatesLoading}
                      onDelete={handleEstimateDelete}
                      onEdit={handleEstimateEdit}
                      onView={handleEstimateView}
                      onConvert={handleEstimateConvert}
                      onStatusChange={handleEstimateStatusChange}
                    />
                  </div>
                )}
              </section>
            </div>
          </div>
        )}
      </div>

      <button
        onClick={() => navigate(-1)}
        className="rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra dark:bg-neutral-900 dark:border-neutral-700 dark:text-white"
      >
        <FiArrowLeft className="inline mr-2" />
        Back to Customers
      </button>

      {/* Customer Form Drawer */}
      <CustomerFormDrawer
        isOpen={editModalOpen}
        onClose={closeEditModal}
        onSave={handleEditSave}
        customer={editForm}
        loading={saving}
        error={error}
      />

      {/* Context Menu */}
      {menuPosition && selectedItem && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 z-50" 
            onClick={handleMenuClose}
          />
          
          {/* Menu */}
          <div 
            className="fixed z-50 bg-white dark:bg-neutral-900 rounded-lg shadow-xl border-2 border-blue-500 dark:border-blue-400 py-2 min-w-[160px]"
            style={{
              left: `${menuPosition.x}px`,
              top: `${menuPosition.y}px`
            }}
            onClick={e => e.stopPropagation()}
          >
            <button
              onClick={handleEdit}
              className="w-full px-4 py-2 text-left text-sm text-neutral-700 dark:text-neutral-300 hover:bg-blue-50 dark:hover:bg-blue-900/20 focus:outline-none focus:bg-blue-50 dark:focus:bg-blue-900/20 transition-colors"
            >
              {selectedItem.type === 'payment' ? 'Edit Payment' : 'Edit Invoice'}
            </button>
            <button
              onClick={handleDelete}
              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 focus:outline-none focus:bg-red-50 dark:focus:bg-red-900/20 transition-colors"
            >
              {selectedItem.type === 'payment' ? 'Void Payment' : 'Delete Invoice'}
            </button>
          </div>
        </>
      )}

      {isWizardModalOpen && (
        <InvoiceFormWizard
          isOpen={isWizardModalOpen}
          onClose={() => setIsWizardModalOpen(false)}
          onSuccess={() => {
            setIsWizardModalOpen(false);
            mutateInvoices();
            fetchCustomerCredit();
          }}
          customerId={customer.id}
        />
      )}

      {isEstimateWizardOpen && (
        <InvoiceFormWizard
          isOpen={isEstimateWizardOpen}
          onClose={() => setIsEstimateWizardOpen(false)}
          onSuccess={() => {
            setIsEstimateWizardOpen(false);
            mutateEstimates();
          }}
          customerId={customer.id}
          mode="estimate"
        />
      )}

      {isPaymentWizardOpen && (
        <PaymentWizard
          isOpen={isPaymentWizardOpen}
          onClose={() => setIsPaymentWizardOpen(false)}
          customerId={customer?.id || ''}
          customerName={customer?.display_name || customer?.company_name || `${customer?.first_name} ${customer?.last_name}`}
          onSuccess={async () => {
            // Ensure SBAL invoice is immediately available for UI math and activity
            await fetchSbal();
            // Refresh both payments and invoices to show updated balances
            refreshCustomer();
            fetchPayments();
            mutateInvoices();
            fetchCustomerCredit();
          }}
        />
      )}

      <ConfirmationDialog
        isOpen={showEstimateStatusWarning}
        title="Warning"
        message="This will convert this estimate into a new invoice and mark it approved. Continue?"
        onConfirm={handleEstimateStatusWarningConfirm}
        onCancel={() => {
          setShowEstimateStatusWarning(false);
          setPendingEstimateStatusChange(null);
        }}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
      />

      {isPaymentEditOpen && selectedPayment && selectedPayment.id !== 'starting-balance-credit' && (
        <PaymentEditDrawer
          isOpen={isPaymentEditOpen}
          onClose={() => {
            setIsPaymentEditOpen(false);
            setSelectedPayment(null);
          }}
          payment={{
            id: selectedPayment.id,
            amount: selectedPayment.amount,
            date: selectedPayment.date,
            method: selectedPayment.method === 'credit_card' ? 'card' : selectedPayment.method,
            reference: selectedPayment.reference || '',
            memo: selectedPayment.memo || '',
            status: selectedPayment.status,
            customer_id: selectedPayment.customer_id || customer.id || '',
            invoice_numbers: selectedPayment.invoice_numbers
          }}
          onSave={handlePaymentEditSave}
          onDelete={handlePaymentDelete}
        />
      )}

      {isPricingModalOpen && customer && (
        <CustomerPricingModal
          isOpen={isPricingModalOpen}
          onClose={() => setIsPricingModalOpen(false)}
          customerId={customer.id}
          customerName={customer.display_name || `${customer.first_name} ${customer.last_name}`}
        />
      )}

      {/* Confirmation Dialogs */}
      <ConfirmationDialog
        isOpen={showPaymentDeleteConfirm}
        title="Void Payment"
        message="Are you sure you want to void this payment? This action cannot be undone."
        confirmLabel="Void Payment"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handlePaymentDeleteConfirm}
        onCancel={handleCancelDelete}
      />

      <ConfirmationDialog
        isOpen={showInvoiceDeleteConfirm}
        title="Delete Invoice"
        message="Are you sure you want to delete this invoice? This action cannot be undone."
        confirmLabel="Delete Invoice"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={handleInvoiceDeleteConfirm}
        onCancel={handleCancelDelete}
      />
    </div>
  );
}; 
